import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, MapPin, ArrowRight } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      alert("Please fill in all required fields.");
      return;
    }

    // Construct WhatsApp message
    const phoneNumber = "+918850227415";
    const text = `*New Inquiry from Website*%0A%0A*Name:* ${formData.name}%0A*Email:* ${formData.email}%0A*Subject:* ${formData.subject}%0A*Message:* ${formData.message}`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${text}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <section
      id="contact"
      className="py-32 bg-background relative overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase mb-4 block">
              Get in Touch
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-8 leading-tight">
              Let's start your <br />
              <span className="italic text-muted-foreground">
                healing journey.
              </span>
            </h2>
            <p className="text-lg text-muted-foreground font-light mb-12 leading-relaxed max-w-md">
              Whether you have a question about our services or need help with a
              plan, we're here to guide you.
            </p>

            <div className="space-y-10">
              <div className="group flex items-start">
                <div className="w-12 h-12 rounded-full border border-border flex items-center justify-center flex-shrink-0 mr-6 group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-all duration-300">
                  <Phone size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-serif font-semibold text-foreground mb-1">
                    Phone
                  </h3>
                  <p className="text-muted-foreground font-light">
                    +91 88502 27415
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-1 uppercase tracking-wider">
                    Mon-Fri • 9am - 6pm
                  </p>
                </div>
              </div>

              <div className="group flex items-start">
                <div className="w-12 h-12 rounded-full border border-border flex items-center justify-center flex-shrink-0 mr-6 group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-all duration-300">
                  <Mail size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-serif font-semibold text-foreground mb-1">
                    Email
                  </h3>
                  <p className="text-muted-foreground font-light">
                    wellnessbymercy@gmail.com
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-1 uppercase tracking-wider">
                    24h Response Time
                  </p>
                </div>
              </div>

              <div className="group flex items-start">
                <div className="w-12 h-12 rounded-full border border-border flex items-center justify-center flex-shrink-0 mr-6 group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-all duration-300">
                  <MapPin size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-serif font-semibold text-foreground mb-1">
                    Location
                  </h3>
                  <p className="text-muted-foreground font-light">
                    Mumbai, Maharashtra
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-1 uppercase tracking-wider">
                    Online Worldwide
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/60 backdrop-blur-xl p-10 md:p-12 rounded-[2rem] shadow-2xl shadow-stone-200/50 border border-white/40"
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-6">
                <div className="relative group">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="peer w-full px-0 py-3 border-b border-stone-300 bg-transparent focus:border-primary focus:outline-none transition-colors placeholder-transparent"
                    placeholder="Name"
                  />
                  <label
                    htmlFor="name"
                    className="absolute left-0 -top-3.5 text-stone-500 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-stone-600 peer-focus:text-sm"
                  >
                    Your Name
                  </label>
                </div>

                <div className="relative group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="peer w-full px-0 py-3 border-b border-stone-300 bg-transparent focus:border-primary focus:outline-none transition-colors placeholder-transparent"
                    placeholder="Email"
                  />
                  <label
                    htmlFor="email"
                    className="absolute left-0 -top-3.5 text-stone-500 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-stone-600 peer-focus:text-sm"
                  >
                    Email Address
                  </label>
                </div>

                <div className="relative group">
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="peer w-full px-0 py-3 border-b border-stone-300 bg-transparent focus:border-primary focus:outline-none transition-colors text-stone-800"
                  >
                    <option value="" disabled>
                      Select a topic
                    </option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Nutrition Consultation">
                      Nutrition Consultation
                    </option>
                    <option value="Yoga Classes">Yoga Classes</option>
                    <option value="Corporate Wellness">
                      Corporate Wellness
                    </option>
                    <option value="Other">Other</option>
                  </select>
                  <label
                    htmlFor="subject"
                    className="absolute left-0 -top-3.5 text-stone-500 text-sm"
                  >
                    Topic
                  </label>
                </div>

                <div className="relative group">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="peer w-full px-0 py-3 border-b border-stone-300 bg-transparent focus:border-primary focus:outline-none transition-colors placeholder-transparent resize-none"
                    placeholder="Message"
                  />
                  <label
                    htmlFor="message"
                    className="absolute left-0 -top-3.5 text-stone-500 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-stone-600 peer-focus:text-sm"
                  >
                    How can we help?
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-stone-900 text-white rounded-full font-medium hover:bg-stone-800 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-3 group"
              >
                <span>Send Message</span>
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
